import { useState } from 'react'
import { transformInteractiveComponentsToMarkdown } from '@/lib/utils/transform-interactive-components'

export interface useCopyToClipboardProps {
  timeout?: number
}

export type CopyToClipboardOptions = {
  exact?: boolean
  htmlText?: string
}

const getCopyOptions = (
  htmlTextOrOptions?: string | CopyToClipboardOptions
): CopyToClipboardOptions => {
  if (typeof htmlTextOrOptions === 'string') {
    return { htmlText: htmlTextOrOptions }
  }

  return htmlTextOrOptions || {}
}

export function useCopyToClipboard({ timeout = 2000 }: useCopyToClipboardProps) {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const copyToClipboard = (
    plainText: string,
    htmlTextOrOptions?: string | CopyToClipboardOptions
  ) => {
    if (typeof window === 'undefined' || !navigator.clipboard) {
      return
    }

    const options = getCopyOptions(htmlTextOrOptions)
    const markCopied = () => {
      setIsCopied(true)

      setTimeout(() => {
        setIsCopied(false)
      }, timeout)
    }

    if (options.exact) {
      const exactWrite =
        typeof navigator.clipboard.writeText === 'function'
          ? navigator.clipboard.writeText(plainText)
          : typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard.write === 'function'
            ? navigator.clipboard.write([
                new ClipboardItem({
                  'text/plain': new Blob([plainText], { type: 'text/plain' })
                })
              ])
            : null

      exactWrite?.then(markCopied)
      return
    }

    // Transform interactive components to markdown format
    const transformedPlainText = transformInteractiveComponentsToMarkdown(plainText)

    // If htmlText is provided, transform it too
    const transformedHtmlText = options.htmlText
      ? transformInteractiveComponentsToMarkdown(options.htmlText)
      : undefined

    // Ensure line breaks are preserved in plain text (replace \n with \r\n for Windows compatibility)
    const normalizedPlainText = transformedPlainText.replace(/\n/g, '\r\n')

    const items = [
      new ClipboardItem({
        'text/plain': new Blob([normalizedPlainText], { type: 'text/plain' }),
        ...(transformedHtmlText && {
          'text/html': new Blob([transformedHtmlText], { type: 'text/html' })
        })
      })
    ]

    navigator.clipboard.write(items).then(markCopied)
  }

  return { isCopied, copyToClipboard }
}
